import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import * as Sentry from '@sentry/angular-ivy';
import {
  AppleHealthService,
  AppleHealthSyncStep,
} from 'src/app/services/apple-health.service';
import { AppService } from 'src/app/services/app.service';
import { TranslateConfigService } from 'src/app/services/translate-config.service';

type UpsellStep = 'promo' | 'syncing' | 'done';

/** `upsell` shows the connect promo; `sync` jumps straight to the progress screen. */
export type AppleHealthUpsellMode = 'upsell' | 'sync';

@Component({
  selector: 'app-apple-health-upsell',
  templateUrl: './apple-health-upsell.component.html',
  styleUrls: ['./apple-health-upsell.component.scss'],
})
export class AppleHealthUpsellComponent implements OnInit {

  @Input() userId: string;
  @Input() mode: AppleHealthUpsellMode = 'upsell';

  public connecting = false;
  public step: UpsellStep = 'promo';
  public syncSteps: AppleHealthSyncStep[] = [];
  public upsellPrefix = 'pages.connectedApps.upsell';

  constructor(
    private appleHealthService: AppleHealthService,
    private appService: AppService,
    private modalCtrl: ModalController,
    private translateConfig: TranslateConfigService,
  ) {}

  ngOnInit() {
    this.upsellPrefix = this.appleHealthService.upsellCopyPrefix();
    this.syncSteps = this.appleHealthService.getDefaultSyncSteps();
    if (this.mode === 'sync') {
      void this.runSync('synced');
    }
  }

  /**
   * Primary CTA after the custom HealthKit / Health Connect explainer.
   * Always continues into the native system permission request (no skip / delay).
   */
  async connect() {
    if (!this.userId || this.connecting) {
      return;
    }
    this.connecting = true;
    try {
      await this.appleHealthService.connect(this.userId);
      await this.appleHealthService.markUpsellSeen();
      await this.runSync('connected');
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: {
          feature: this.appleHealthService.isNativeAndroid() ? 'google-health' : 'apple-health',
          step: 'upsell-connect',
        },
      });
      this.step = 'promo';
      this.connecting = false;
      this.syncSteps = this.appleHealthService.getDefaultSyncSteps();
      // Android: offer Health Connect settings when the APK / permissions path failed.
      // (Separate from the first-request path — Apple allows Settings only after denial.)
      if (this.appleHealthService.isNativeAndroid()) {
        void this.appleHealthService.openHealthConnectSettings();
      }
      this.appService.showToastWithDuration(
        this.translateConfig.t(
          this.appleHealthService.isNativeAndroid()
            ? 'pages.connectedApps.googleConnectError'
            : 'pages.connectedApps.connectError'
        ),
        null,
        'danger'
      );
    }
  }

  /** Runs the shared sync progress UI, then dismisses with the given role. */
  private async runSync(dismissRole: 'connected' | 'synced'): Promise<void> {
    if (!this.userId) {
      return;
    }
    this.connecting = true;
    this.step = 'syncing';
    this.syncSteps = this.appleHealthService.getDefaultSyncSteps();
    try {
      await this.appleHealthService.syncAll(this.userId, (steps) => {
        this.syncSteps = steps;
      });
      this.step = 'done';
      await this.pause(1400);
      await this.modalCtrl.dismiss(null, dismissRole);
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: {
          feature: this.appleHealthService.isNativeAndroid() ? 'google-health' : 'apple-health',
          step: this.mode === 'sync' ? 'manual-sync' : 'upsell-sync',
        },
      });
      this.connecting = false;
      this.appService.showToastWithDuration(
        this.translateConfig.t(
          this.appleHealthService.isNativeAndroid()
            ? 'pages.connectedApps.googleConnectError'
            : 'pages.connectedApps.connectError'
        ),
        null,
        'danger'
      );
      if (this.mode === 'sync') {
        await this.modalCtrl.dismiss(null, 'error');
      } else {
        this.step = 'promo';
        this.syncSteps = this.appleHealthService.getDefaultSyncSteps();
      }
    }
  }

  async notNow() {
    if (this.connecting) {
      return;
    }
    try {
      if (this.userId) {
        await this.appleHealthService.dismissPrompt(this.userId);
      }
    } catch (error) {
      Sentry.captureException(error?.originalError || error, {
        tags: {
          feature: this.appleHealthService.isNativeAndroid() ? 'google-health' : 'apple-health',
          step: 'upsell-dismiss',
        },
      });
    }
    this.appleHealthService.showDismissedHint();
    await this.modalCtrl.dismiss(null, 'dismissed');
  }

  metricLabelKey(key: string): string {
    // Metric labels are shared across Apple / Google upsell copy.
    return `pages.connectedApps.upsell.metrics.${key}`;
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
